import { forwardRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { NavigationAnchorItem } from './elements'

/**
 * Returns true if the current location matches a configured path.
 *
 * Paths that include a `?` are matched against the full `pathname + search`
 * so that query-string-differentiated views (e.g. `?view=integrations`) can
 * each have their own distinct active state. Paths without `?` use only
 * `pathname` to preserve existing behaviour for pages that carry transient
 * query params (filters, pagination, etc.).
 */
const matchesPath = (configuredPath, pathname, search) => {
  if (configuredPath.includes('?')) {
    return (pathname + search) === configuredPath
  }
  return pathname === configuredPath
}

/**
 * Nav link wrapper; forwards ref to the underlying anchor for click forwarding from parent.
 *
 * @param {Object} props
 * @param {string} props.to - Path to link to
 * @param {string[]} [props.activePaths] - Additional paths that should activate this item
 * @param {string[]} [props.activePathPrefixes] - Path prefixes (pathname.startsWith) that count as active
 * @param {string[]} [props.inactivePaths] - Paths that force this item inactive even if `to`/`activePaths` match
 * @param {React.ReactNode} props.children - Icon, label, etc.
 * @param {React.React.RefObject} ref - Ref forwarded to the anchor element
 * @returns {JSX.Element}
 */
export const NavigationItem = forwardRef(({
  to,
  activePaths = [],
  activePathPrefixes,
  inactivePaths = [],
  children,
  ...props
}, ref) => {
  const { pathname, search } = useLocation()

  const isActive = useMemo(() => {
    if (activePathPrefixes?.length) {
      const prefixHit = activePathPrefixes.some((prefix) => pathname.startsWith(prefix))
      if (prefixHit) return true
    }
    const wouldBeActive = [].concat(to, activePaths).some((p) => matchesPath(p, pathname, search))
    if (!wouldBeActive) return false
    return !inactivePaths.some((p) => matchesPath(p, pathname, search))
  }, [pathname, search, to, activePaths, activePathPrefixes, inactivePaths])

  return (
    <NavigationAnchorItem
      ref={ref}
      as={Link}
      isActive={isActive}
      to={to}
      {...props}
    >
      {children}
    </NavigationAnchorItem>
  )
})
